import React, { useState } from "react";
import { Cake, Heart, MessageCircle, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { AuthImage } from "./FileUpload";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";

const waLink = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;

function useWish(b, onWish) {
  const [busy, setBusy] = useState(false);
  const first = b.name.split(" ")[0];
  const wish = async () => {
    setBusy(true);
    try { const { data } = await api.post("/members/birthdays/wish", { member_id: b.id }); toast.success(data.already ? `You already wished ${first} this month` : `Your wishes were sent to ${first} 🎂`); onWish(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return { busy, wish };
}

function WishButton({ b, busy, onClick }) {
  if (b.is_me) return null;
  const done = b.wished_by_me;
  return (
    <button onClick={onClick} disabled={busy || done} className={`mt-2 inline-flex min-h-[36px] w-full items-center justify-center gap-1 rounded-full px-3 text-xs font-semibold transition-colors ${done ? "bg-palm/15 text-palm" : "bg-crimson text-parchment hover:bg-crimson-hover"}`} data-testid={`birthday-wish-${b.id}`}>
      <PartyPopper size={13} /> {wishLabel(done, busy)}
    </button>
  );
}

const wishLabel = (done, busy) => {
  if (done) return "Wished";
  return busy ? "Sending…" : "Send wishes";
};
const TONES = { today: "border-gold bg-gold-light shadow-md", past: "border-gold/10 bg-parchment-card/60 opacity-80", upcoming: "border-gold/20 bg-parchment-card" };
const cardTone = (b) => TONES[b.is_today ? "today" : b.is_past ? "past" : "upcoming"];

function Celebrant({ b, month, onWish, t }) {
  const { busy, wish } = useWish(b, onWish);
  return (
    <div className={`flex w-40 shrink-0 snap-start flex-col items-center rounded-2xl border p-4 text-center transition-transform hover:-translate-y-0.5 ${cardTone(b)}`} data-testid={`birthday-${b.id}`}>
      <div className={`flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-crimson text-2xl font-bold text-parchment ${b.is_today ? "ring-4 ring-gold" : "ring-2 ring-gold/40"}`}>
        {b.photo_url ? <AuthImage src={b.photo_url} alt="" className="h-full w-full object-cover" /> : b.name[0]}
      </div>
      <p className="mt-3 line-clamp-2 text-sm font-semibold leading-tight text-crimson">{b.name}{b.is_me ? ` (${t("you")})` : ""}</p>
      <p className="text-xs text-muted-foreground">{month} {b.day}{b.is_today ? ` · ${t("Today!")} 🎉` : ""}</p>
      <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-palm" data-testid={`birthday-wishes-${b.id}`}><Heart size={11} className={b.wishes ? "fill-palm" : ""} /> {b.wishes} {b.wishes === 1 ? "wish" : "wishes"}</p>
      <WishButton b={b} busy={busy} onClick={wish} />
    </div>
  );
}

export function BirthdayStrip() {
  const { t } = useLang();
  const { isOfficer } = useAuth();
  const { data, reload } = useApi("/members/birthdays");
  if (!data) return null;
  const list = data.birthdays;
  const share = `🎂 ${data.month} birthdays — Okuapemman Association of Massachusetts:\n${list.map((b) => `• ${b.name} — ${data.month} ${b.day}`).join("\n")}\nLet's celebrate our members! Ofie! Biakoye!`;
  return (
    <div className="glass mt-6 rounded-2xl p-6" data-testid="birthday-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Cake size={20} className="text-crimson" />
          <h3 className="font-serif-display text-xl font-bold text-crimson">{t("Birthdays in")} {data.month}</h3>
        </div>
        {list.length > 0 && isOfficer && <a href={waLink(share)} target="_blank" rel="noreferrer" className="inline-flex min-h-[36px] items-center gap-1 rounded-full bg-[#25D366] px-3 text-xs font-semibold text-white hover:bg-[#1ebe5a]" data-testid="birthday-share-wa"><MessageCircle size={13} /> Share on WhatsApp</a>}
      </div>
      {list.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">{t("No member birthdays this month.")}</p> : (
        <>
          <p className="mt-1 text-xs text-muted-foreground">Tap "Send wishes" to send a birthday greeting straight to their phone.</p>
          <div className="mt-4 flex snap-x gap-3 overflow-x-auto pb-2 sidebar-scroll" data-testid="birthday-strip">
            {list.map((b) => <Celebrant key={b.id} b={b} month={data.month} onWish={reload} t={t} />)}
          </div>
        </>
      )}
    </div>
  );
}
